/**
 * 구독(정기결제) 화면이 오가는 서버 모양 — 네 화면이 같이 쓴다.
 * 서버는 `apps/api/src/domain/billing/*`. 설계는 `docs/상품_구독_설계.md` §3.
 */

/** 결제 주기 한 줄 (`tb_billing_product.cycle_price` 의 한 항목) */
export interface BillingCycle {
  /** 주기 번호 — 구독자·스케줄·이력이 참조하는 값이다(순번이 아니라 키다) */
  key: number;
  type: 'day' | 'month' | 'year';
  period: number;
  price: number;
}

/** 지원 기기 — `tool_use`(1 전체 / 2 PC / 3 모바일) */
export type ToolUse = 'ALL' | 'PC' | 'MOBILE';

/** GET /billing/plans 한 줄 */
export interface BillingPlan {
  id: number;
  name: string;
  /** 추천 배지(BEST·HIT·NEW·EVENT) — 카드 왼쪽 위 */
  badges: string[];
  image: string | null;
  /** 등급 — 업그레이드 판정에 쓰는 값(화면은 안 적는다) */
  rank: number;
  /** 이 구독을 살 때 쿠폰을 쓸 수 있나 — 끄면 결제 화면에 쿠폰 칸이 안 나온다 */
  couponEnabled: boolean;
  /** 포인트를 쓸 수 있나 — 끄면 포인트 칸이 안 나온다 */
  pointEnabled: boolean;
  cycles: BillingCycle[];
  /**
   * 이용 범위 — 두 갈래다(§3-1).
   * `PTYPE` 이면 종류(단과·패키지…) 전체, `PICK` 이면 지정한 상품만.
   */
  target: {
    kind: 'PTYPE' | 'PICK';
    ptypes: string[];
    products: { id: number; name: string; type: string }[];
  };
  toolUse: ToolUse;
  toolPc: number | null;
  toolMobile: number | null;
  deviceResetLimit: number | null;
}

/** GET /billing/methods 한 줄 */
export interface BillingMethod {
  billingKey: string;
  cardName: string | null;
  /** 마스킹된 번호 — 토스가 준 그대로다 */
  cardNumber: string | null;
  isForeign: boolean;
  /**
   * 자리 — 1=주 카드, 2=보조 카드, 3=미지정(어느 자리에도 없음).
   * **자리마다 한 장씩**이다. 재결제는 주 → 보조 순으로 긁고 미지정은 쓰지 않는다.
   */
  priority: number;
  createdAt: string;
}

/** GET /billing/my — 구독 중이 아니면 `null` */
export interface MySubscription {
  id: number;
  cycle: number | null;
  count: number | null;
  startDate: string | null;
  endDate: string | null;
  nextPayDate: string | null;
  billingKey: string | null;
  /** 해지 신청일 — 있으면 "해지 예정"이다(기간까지는 이용한다) */
  cancelRequestedAt: string | null;
  deviceLimitPc: number | null;
  deviceLimitMobile: number | null;
  deviceResetLimit: number | null;
  plan: {
    id: number;
    name: string;
    /** 등급 — 고른 플랜과 견줘 상향·하향을 가린다(숫자가 클수록 상위) */
    rank: number;
    cycles: BillingCycle[];
    toolUse: ToolUse;
    toolPc: number | null;
    toolMobile: number | null;
    deviceResetLimit: number | null;
  };
  /**
   * 다음에 긁힐 카드 = **주 카드**. 재결제가 주 → 보조 순으로 시도한다.
   * 회차마다 실제로 결제된 카드는 주문(`tb_billing_order`)에 남는다.
   */
  method: { billingKey: string; cardName: string | null; cardNumber: string | null; active: boolean } | null;
  schedule: { payDate: string | null; nextProductId: number | null; nextCycleKey: number | null; paid: boolean } | null;
  history: {
    id: number;
    orderId: number;
    paidAt: string;
    cyclePrice: number | null;
    cycleType: string | null;
    cyclePeriod: number | null;
    status: string;
    /** 영수증(토스 대시보드) — 무료 구독처럼 PG 를 안 탄 회차는 없다 */
    receiptUrl: string | null;
  }[];
}

/**
 * 주기 표기 — `1개월`·`32일`·`1년`. 가이드 `price-label` 자리다.
 * 사전 키를 쓰는 이유는 마켓 언어가 넷이기 때문이다(숫자만 갈아 끼운다).
 */
export function cycleLabel(c: BillingCycle, t: (k: string, v?: Record<string, string>) => string): string {
  const n = String(c.period);
  return c.type === 'day' ? t('sub.cycle.day', { n })
    : c.type === 'year' ? t('sub.cycle.year', { n })
      : t('sub.cycle.month', { n });
}
